<template>
  <div class="pa-4 survey-form-container">
    <div class="survey-title-description-container">
      <h2 class="survey-title">{{ surveyPublicDetails.titleForConsumer }}</h2>
      <h3 class="survey-description">
        {{ surveyPublicDetails.descriptionForConsumer }}
      </h3>
    </div>

    <div v-if="loadingQuestions" class="questions-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else class="questions-container">
      <div v-for="question in questions" :key="question.id" class="base-question">
        <span class="question-title">{{ question.title }}</span>
        <span class="question-subtitle">{{ question.subtitle }}</span>
        <component
          :is="questionTypeComponentMap[question.type]"
          :ref="`question-${question.id}-component`"
          :question="question"
          @change="handleChange(question.id, $event)"
        />
        <span class="clear-answer-button" @click="clearAnswer(question.id)">Limpar resposta</span>
      </div>

      <v-btn
        class="survey-form-button"
        elevation="1"
        :color="assets.colors.form.buttonBackground"
        :style="{ color: assets.colors.form.buttonFont }"
        :loading="isSubmittingForm"
        :disabled="!isThereAnyAnswer"
        @click="submitAnswers"
      >
        Enviar resposta
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import store from '@/vuex/store'
import { SurveyService } from '@/services/survey.service'
import SingleChoice from '@/modules/survey/views/components/SingleChoice.vue'
import MultipleChoice from '@/modules/survey/views/components/MultipleChoice.vue'
import OpenAnswer from '@/modules/survey/views/components/OpenAnswer.vue'
import CsatAnswer from '@/modules/survey/views/components/CsatAnswer.vue'
import NpsAnswer from '@/modules/survey/views/components/NpsAnswer.vue'

export default {
  name: 'SurveyForm',
  components: {
    SingleChoice,
    MultipleChoice,
    OpenAnswer,
    CsatAnswer,
    NpsAnswer
  },
  data() {
    return {
      questions: [],
      loadingQuestions: true,
      isSubmittingForm: false
    }
  },
  computed: {
    ...mapGetters(['GET_ASSETS']),
    ...mapGetters('survey', ['GET_SURVEY_ACCOUNT_ID', 'GET_SURVEY_SELECTED_UNIT', 'GET_SURVEY_TOKEN', 'GET_SURVEY_PUBLIC_DETAILS']),
    assets() {
      return this.GET_ASSETS
    },
    surveyPublicDetails() {
      return this.GET_SURVEY_PUBLIC_DETAILS || {}
    },
    surveyId() {
      return this.$route.params.surveyId
    },
    surveyAccountId() {
      return this.GET_SURVEY_ACCOUNT_ID
    },
    surveySelectedUnit() {
      return this.GET_SURVEY_SELECTED_UNIT
    },
    surveyToken() {
      return this.GET_SURVEY_TOKEN
    },
    questionTypeComponentMap() {
      return {
        SINGLE_CHOICE: SingleChoice,
        MULTIPLE_CHOICE: MultipleChoice,
        OPEN: OpenAnswer,
        CSAT: CsatAnswer,
        NPS: NpsAnswer
      }
    },
    isThereAnyAnswer() {
      return this.questions.some(question => question?.answers?.length)
    }
  },
  beforeRouteEnter(to, from, next) {
    // Must redirect here (sync), not inside next(callback): that callback runs after
    // mounted(), so fetchQuestions would fire once without a token before redirect.
    const token = store.getters['survey/GET_SURVEY_TOKEN']
    if (!token) {
      next({ name: 'survey-id-entry', params: { surveyId: to.params.surveyId } })
      return
    }
    next()
  },
  async mounted() {
    try {
      const surveyService = new SurveyService(this.surveyAccountId, this.surveyToken)
      this.questions = await surveyService.fetchQuestions(this.surveyId)
    } catch {
      this.$snackbar({
        message: 'Não foi possível carregar as perguntas. Tente novamente.',
        snackbarColor: 'error'
      })
      this.$router.push({ name: 'survey-id-entry', params: { surveyId: this.surveyId } })
    } finally {
      this.loadingQuestions = false
    }
  },
  methods: {
    async submitAnswers() {
      const { isValid, firstInvalidQuestion } = this.validateForm()
      if (!isValid) {
        this.$snackbar({
          message: 'Preencha todas as perguntas corretamente.',
          snackbarColor: 'error',
          timeout: 3000
        })
        this.$nextTick(() => {
          if (firstInvalidQuestion) {
            firstInvalidQuestion.$el.focus()
            firstInvalidQuestion.$el.scrollIntoView({ behavior: 'smooth' })
          }
        })
        return
      }

      this.isSubmittingForm = true

      try {
        const surveyService = new SurveyService(this.surveyAccountId, this.surveyToken)

        await surveyService.answerQuestions({
          unitId: this.surveySelectedUnit,
          questions: this.mountQuestionsSubmitData()
        })

        this.$router.push({ name: 'survey-information', query: { type: 'success' } })
      } catch (error) {
        console.error('Error: ', error)
        this.$snackbar({
          message: 'Ocorreu um erro ao enviar as respostas. Tente novamente.',
          snackbarColor: 'error'
        })
      } finally {
        this.isSubmittingForm = false
      }
    },

    mountQuestionsSubmitData() {
      const formattedSubmitData = []
      this.questions.forEach(question => {
        let formattedQuestion = {}
        if (!question?.answers?.length) return
        formattedQuestion.questionId = question.id
        formattedQuestion.questionType = question.type

        if (question.type === 'SINGLE_CHOICE') {
          formattedQuestion.answer = {
            choice: question?.answers?.[0]
          }
        } else if (question.type === 'MULTIPLE_CHOICE') {
          formattedQuestion.answer = {
            choices: question?.answers
          }
        } else if (question.type === 'OPEN') {
          formattedQuestion.answer = {
            text: question?.answers
          }
        } else if (question.type === 'CSAT' || question.type === 'NPS') {
          formattedQuestion.answer = {
            rate: question?.answers?.[0]
          }
        }
        formattedSubmitData.push(formattedQuestion)
      })
      return formattedSubmitData
    },

    clearAnswer(questionId) {
      this.questions = this.questions.map(question => {
        if (question.id === questionId) {
          return {
            ...question,
            answers: []
          }
        }
        return question
      })
    },

    handleChange(questionId, value) {
      this.questions = this.questions.map(question => {
        if (question.id === questionId) {
          return { ...question, answers: value }
        }
        return question
      })
    },

    validateForm() {
      let isValid = true
      let firstInvalidQuestion = null
      this.questions.forEach(question => {
        const questionComponent = this.$refs[`question-${question.id}-component`]?.[0]
        if (questionComponent?.invalidAnswer) {
          isValid = false
          firstInvalidQuestion = questionComponent
        }
      })
      return { isValid, firstInvalidQuestion }
    }
  }
}
</script>

<style scoped lang="scss">
.survey-form-container {
  background-color: #f6f6f8;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.survey-title-description-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  max-width: 846px;
  margin-top: 70px;

  @media screen and (max-width: 768px) {
    margin-top: 35px;
  }
}

.survey-title {
  font-weight: bold;
  font-size: 36px;
  text-align: center;
  color: #4b525a;
}

.survey-description {
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
  text-align: center;
}

.questions-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  width: 100%;
  padding: 40px 64px;
  border-radius: 8px;
  background-color: white;
  max-width: 846px;

  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
}

.base-question {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
}

.question-title {
  font-weight: bold;
  font-size: 18px;
  color: #4b525a;
}

.question-subtitle {
  font-weight: normal;
  font-size: 14px;
  color: #6b7580;
}

.survey-form-button {
  align-self: flex-end;
  min-width: 300px !important;

  @media screen and (max-width: 768px) {
    align-self: center;
  }
}

.clear-answer-button {
  font-weight: normal;
  font-size: 14px;
  text-decoration: underline;
  text-decoration-style: solid;
  color: #31363b;
  user-select: none;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
}
</style>
